import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { cancelSubscription, changePassword, createCheckoutSession, deleteAccount, getUser } from "../api/thunks/users";
import { getSecretValue, createSecret, deleteSecret, updateSecret, getSecrets } from "../api/thunks/secrets"; // Ensure updateSecret is imported
import { ToolbarComponent } from "./toolbar-component";
import { Conversation } from "../api/models/Conversation";
import { setNotification } from "../store/notifications-reducer";
import { CheckBadgeIcon, InformationCircleIcon } from "@heroicons/react/24/solid";
import { Eye, Plus, Check, EyeOff, Trash, Pencil, X, ChartLine, KeyRound, ExternalLink } from "lucide-react";
import { Secret } from "../api/models/Secret";
import { Integration, IntegrationType } from "../api/models/Integration";
import RedditIcon from "./reddit-icon";
import { disconnectRedditIntegration, getRedditIntegrationUrl } from "../api/thunks/integrations";
import { signOut } from "../utils/auth";

const alphaVantageSecretName = 'integration.alpha_vantage.api_key';

export const ProfileComponent = ({
    handleUpdateConversation,
    convos,
    convo,
    handleCreateConversation,
    handleDeleteConversation,
    supportedTickers,
    handleDeleteShareLink,
    handleCreateShareLink,
    secrets = [],
    setSecrets
}: {
    handleUpdateConversation: (id: string, name: string) => void,
    convos: Conversation[],
    convo: Conversation | undefined,
    handleCreateConversation: (ticker: string) => Promise<void>,
    handleDeleteConversation: (id: string) => Promise<void>,
    supportedTickers: string[],
    handleDeleteShareLink: (conversationId: string) => void,
    handleCreateShareLink: (conversationId: string) => void,
    secrets?: Secret[]
    setSecrets: (secrets: Secret[]) => void
}) => {
    document.title = "Profile | PocketQuant";
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const credentials = useAppSelector((state) => state.user.credentials);
    const user = useAppSelector((state) => state.user.user);
    const [isChangingPassword, setIsChangingPassword] = useState<boolean>(false);

    const [unsubscribeModalOpen, setUnsubscribeModalOpen] = useState(false);
    const [currentPassword, setCurrentPassword] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');

    // Add state for revealed secrets
    const [revealedSecrets, setRevealedSecrets] = useState<Record<string, string>>({});
    const [loadingSecrets, setLoadingSecrets] = useState<Record<string, boolean>>({});

    // Add state for creating new secrets
    const [showNewSecretForm, setShowNewSecretForm] = useState(false);
    const [newSecretName, setNewSecretName] = useState('');
    const [newSecretValue, setNewSecretValue] = useState('');
    const [isCreatingSecret, setIsCreatingSecret] = useState(false);

    // Add state for editing secrets
    const [editingSecretId, setEditingSecretId] = useState<string | null>(null);
    const [editingSecretName, setEditingSecretName] = useState('');
    const [editingSecretValue, setEditingSecretValue] = useState('');
    const [isUpdatingSecret, setIsUpdatingSecret] = useState(false);

    const [showAlphaVantageForm, setShowAlphaVantageForm] = useState(false);
    const [alphaVantageKey, setAlphaVantageKey] = useState('');
    const [isSavingAlphaVantage, setIsSavingAlphaVantage] = useState(false);

    const alphaVantageSecret = secrets.find((secret) => secret.name === alphaVantageSecretName);
    const visibleSecrets = secrets.filter((secret) => secret.name !== alphaVantageSecretName);

    useEffect(() => {
        if (!credentials) {
            signOut(dispatch);
            return;
        }
    }, [credentials]);

    useEffect(() => {
        dispatch(getUser());
        const params = new URLSearchParams(window.location.search);

        if (params.get('payment_success') === 'false') {
            dispatch(setNotification({ message: 'Payment failed', type: 'error' }));
        }

        if (params.get('payment_success') === 'true') {
            dispatch(setNotification({ message: 'Payment successful', type: 'success' }));
        }
    }, []);

    const handleCreateSubscription = async (priceID?: string) => {
        const res = await dispatch(createCheckoutSession({ productCode: priceID! }));
        if (res.meta.requestStatus === 'fulfilled') {
            window.location.href = (res.payload as any).redirectUrl;
        } else {
            dispatch(setNotification({ message: 'Failed to create checkout session', type: 'error' }));
        }
    }

    const handleChangePassword = async () => {
        if (!isChangingPassword) {
            setIsChangingPassword(true);
            return;
        }

        if (newPassword !== confirmPassword) {
            dispatch(setNotification({ message: 'Passwords do not match', type: 'error' }));
            return;
        }

        const res = await dispatch(changePassword({ currentPassword: currentPassword, newPassword: newPassword }));

        if (res.meta.requestStatus === 'fulfilled') {
            dispatch(setNotification({ message: 'Password changed successfully', type: 'success' }));
            setIsChangingPassword(false);
        } else {
            dispatch(setNotification({ message: 'Unable to change password', type: 'error' }));
        }
    }

    const handleDeleteAccount = async () => {
        if (user?.hasActiveSubscription === true) {
            dispatch(setNotification({ message: 'Cancel your subscription before deleting your account', type: 'error' }));
            return;
        }

        const deleteConfirmation = window.confirm('Are you sure you want to delete your account? This action cannot be undone.');
        if (!deleteConfirmation) {
            return;
        }

        const res = await dispatch(deleteAccount());
        if (res.meta.requestStatus !== 'fulfilled') {
            dispatch(setNotification({ message: 'Unable to delete your account. Try again or contact support.', type: 'error' }));
            return;
        }
        dispatch(setNotification({ message: 'Your account has been deleted.', type: 'success' }));
        signOut(dispatch);
    }

    // Add function to handle revealing secrets
    const handleRevealSecret = async (secretId: string) => {
        if (revealedSecrets[secretId] || editingSecretId === secretId) return; // Don't reveal if already revealed or editing
        if (!credentials?.accessToken) return; // No access token

        setLoadingSecrets(prev => ({ ...prev, [secretId]: true }));

        try {
            const secret = await getSecretValue(secretId, credentials!.accessToken!);
            if (secret) {
                setRevealedSecrets(prev => ({
                    ...prev,
                    [secretId]: secret.value
                }));
            } else {
                dispatch(setNotification({ message: 'Failed to retrieve secret', type: 'error' }));
            }
        } catch (error) {
            console.error('Error revealing secret:', error);
        } finally {
            setLoadingSecrets(prev => ({ ...prev, [secretId]: false }));
        }
    };

    // Add function to handle creating a new secret
    const handleCreateSecret = async () => {
        if (!credentials?.accessToken) return; // No access token

        if (!newSecretName || !newSecretValue) {
            dispatch(setNotification({ message: 'Secret name and value are required', type: 'error' }));
            return;
        }

        setIsCreatingSecret(true);


        createSecret({
            id: '',
            name: newSecretName,
            value: newSecretValue
        }, credentials!.accessToken!).then((result) => {
            dispatch(setNotification({ message: 'Secret created successfully', type: 'success' }));
            setNewSecretName('');
            setNewSecretValue('');
            setShowNewSecretForm(false);
            getSecrets(credentials!.accessToken!).then((secrets) => {
                setSecrets(secrets);
            }).catch((error) => {
                console.error('Error fetching secrets:', error);
                dispatch(setNotification({ message: 'Failed to fetch secrets after creation', type: 'error' }));
            });
        }).catch((error => {
            console.error('Error creating secret:', error);
            if (error?.response?.data?.message) {
                dispatch(setNotification({ message: `Error creating secret: ${error.response.data.message}`, type: 'error' }));
                return;
            }
            dispatch(setNotification({ message: 'Failed to create secret' + error, type: 'error' }));
        })).finally(() => {
            setIsCreatingSecret(false);
        });

    };

    // Add function to handle deleting a secret
    const handleDeleteSecret = async (event: React.MouseEvent, secretId: string) => {
        event.stopPropagation(); // Prevent triggering the reveal secret function

        if (!credentials?.accessToken) return;

        const confirmDelete = window.confirm('Are you sure you want to delete this secret?');
        if (!confirmDelete) return;

        // If deleting the secret currently being edited, cancel edit mode
        if (editingSecretId === secretId) {
            handleCancelEdit();
        }

        deleteSecret(secretId, credentials.accessToken).then(() => {
            // Remove from revealed secrets if it was revealed
            if (revealedSecrets[secretId]) {
                setRevealedSecrets(prev => {
                    const newState = { ...prev };
                    delete newState[secretId];
                    return newState;
                });
            }

            const filtered = secrets.filter(s => s.id !== secretId);
            setSecrets(filtered);

            dispatch(setNotification({ message: 'Secret deleted successfully', type: 'success' }));
        }).catch((error) => {
            if (error?.response?.data?.message) {
                dispatch(setNotification({ message: `Failed to delete secret: ${error.response.data.message}`, type: 'error' }));
            } else {
                console.error('Error deleting secret:', error);
            }
        })
    };

    // Function to start editing a secret
    const handleEditSecret = (event: React.MouseEvent, secret: Secret) => {
        event.stopPropagation(); // Prevent triggering reveal
        setEditingSecretId(secret.id);
        setEditingSecretName(secret.name);
        // Use revealed value if available, otherwise fetch it (or show placeholder/require reveal first)
        // For simplicity, let's assume we edit without seeing the value initially, or use the revealed value if present
        setEditingSecretValue(revealedSecrets[secret.id] || ''); // Or fetch if needed
        // Hide revealed value when starting edit
        if (revealedSecrets[secret.id]) {
            setRevealedSecrets(prev => {
                const newState = { ...prev };
                delete newState[secret.id];
                return newState;
            });
        }
    };

    // Function to cancel editing
    const handleCancelEdit = (event?: React.MouseEvent) => {
        event?.stopPropagation();
        setEditingSecretId(null);
        setEditingSecretName('');
        setEditingSecretValue('');
        setIsUpdatingSecret(false);
    };

    // Function to update a secret
    const handleUpdateSecret = async (event: React.MouseEvent) => {
        event.stopPropagation();
        if (!editingSecretId || !credentials?.accessToken) return;

        if (!editingSecretName || !editingSecretValue) {
            dispatch(setNotification({ message: 'Secret name and value are required', type: 'error' }));
            return;
        }

        setIsUpdatingSecret(true);

        updateSecret({
            id: editingSecretId,
            name: editingSecretName,
            value: editingSecretValue
        }, credentials.accessToken)
            .then((updatedSecret) => {
                // Update the secret in the local state
                // setSecrets((prevSecrets : Secret[]) => prevSecrets.map(s =>
                //     s.id === editingSecretId ? { ...s, name: updatedSecret.name } : s // Update name, value is not returned by update
                // ));
                // Optionally reveal the new value or keep it hidden
                // setRevealedSecrets(prev => ({ ...prev, [editingSecretId]: editingSecretValue }));
                dispatch(setNotification({ message: 'Secret updated successfully', type: 'success' }));
                handleCancelEdit(); // Exit edit mode
            })
            .catch((error) => {
                console.error('Error updating secret:', error);
                if (error?.response?.data?.message) {
                    dispatch(setNotification({ message: `Error updating secret: ${error.response.data.message}`, type: 'error' }));
                } else {
                    dispatch(setNotification({ message: 'Failed to update secret', type: 'error' }));
                }
            })
            .finally(() => {
                setIsUpdatingSecret(false);
            });
    };

    const refreshSecretsAndIntegrations = async () => {
        if (!credentials?.accessToken) return;
        const updatedSecrets = await getSecrets(credentials.accessToken);
        setSecrets(updatedSecrets);
        dispatch(getUser());
    };

    const handleSaveAlphaVantage = async () => {
        if (!credentials?.accessToken) return;
        if (!alphaVantageKey.trim()) {
            dispatch(setNotification({ message: 'An Alpha Vantage API key is required', type: 'error' }));
            return;
        }

        setIsSavingAlphaVantage(true);
        try {
            if (alphaVantageSecret) {
                await updateSecret({
                    id: alphaVantageSecret.id,
                    name: alphaVantageSecretName,
                    value: alphaVantageKey.trim(),
                }, credentials.accessToken);
            } else {
                await createSecret({
                    id: '',
                    name: alphaVantageSecretName,
                    value: alphaVantageKey.trim(),
                }, credentials.accessToken);
            }
            await refreshSecretsAndIntegrations();
            setAlphaVantageKey('');
            setShowAlphaVantageForm(false);
            dispatch(setNotification({ message: 'Alpha Vantage connected', type: 'success' }));
        } catch (error: any) {
            const message = error?.response?.data?.message || 'Failed to connect Alpha Vantage';
            dispatch(setNotification({ message, type: 'error' }));
        } finally {
            setIsSavingAlphaVantage(false);
        }
    };

    const handleDisconnectAlphaVantage = async () => {
        if (!credentials?.accessToken || !alphaVantageSecret) return;
        if (!window.confirm('Disconnect Alpha Vantage? PocketQuant will no longer be able to use this API key.')) return;

        setIsSavingAlphaVantage(true);
        try {
            await deleteSecret(alphaVantageSecret.id, credentials.accessToken);
            await refreshSecretsAndIntegrations();
            setAlphaVantageKey('');
            setShowAlphaVantageForm(false);
            dispatch(setNotification({ message: 'Alpha Vantage disconnected', type: 'success' }));
        } catch (error: any) {
            const message = error?.response?.data?.message || 'Failed to disconnect Alpha Vantage';
            dispatch(setNotification({ message, type: 'error' }));
        } finally {
            setIsSavingAlphaVantage(false);
        }
    };

    const getIntegrationIcon = (t: IntegrationType, status: string) => {
        switch (t) {
            case 'reddit':
                return (
                    <div key="reddit-integration" className={`profile-integration-card rounded-xl border p-5 transition-colors ${status === 'connected' ? 'border-emerald-200 bg-emerald-50/60' : 'border-slate-200 bg-slate-50'}`}>
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex gap-3">
                                <div className={`mt-0.5 rounded-lg p-2 ${status === 'connected' ? 'bg-emerald-600' : 'bg-slate-800'}`}>
                                    <RedditIcon className="h-5 w-5" color="#ffffff" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-semibold text-slate-900">Reddit</h3>
                                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${status === 'connected' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}>
                                            {status === 'connected' ? 'Connected' : 'Not connected'}
                                        </span>
                                    </div>
                                    <p className="mt-1 text-sm leading-5 text-slate-600">Connect Reddit to bring relevant market discussion into your research.</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                className={`text-sm font-medium ${status === 'connected' ? 'text-rose-700 hover:text-rose-900' : 'text-indigo-700 hover:text-indigo-900'}`}
                                onClick={() => {
                                    if (status === 'connected') {
                                        const shouldDisconnect = window.confirm('Are you sure you want to disconnect your Reddit account?');
                                        if (!shouldDisconnect) return;
                                        disconnectRedditIntegration(credentials?.accessToken!).then(() => {
                                            dispatch(setNotification({ message: 'Reddit account disconnected successfully', type: 'success' }));
                                            dispatch(getUser());
                                        }).catch((error) => {
                                            console.error('Error disconnecting Reddit account:', error);
                                            dispatch(setNotification({ message: 'Failed to disconnect Reddit account', type: 'error' }));
                                        });
                                        return;
                                    }
                                    getRedditIntegrationUrl(credentials?.accessToken!).then((url) => {
                                        window.open(url, "_blank");
                                    }).catch((error) => {
                                        console.error('Error getting Reddit integration URL', error);
                                        dispatch(setNotification({ message: 'Failed to get Reddit integration URL', type: 'error' }));
                                    });
                                }}
                            >
                                {status === 'connected' ? 'Disconnect' : 'Connect'}
                            </button>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    }

    return (
        <>
            <CancelSubscriptionModal isOpen={unsubscribeModalOpen} setIsOpen={setUnsubscribeModalOpen} />

            <div className="profile-workspace flex flex-col h-screen">
                {/* Main content area, scrollable if needed */}
                <div className="flex-1 overflow-y-auto">
                    <div className="p-4 md:p-8">
                        <div className="profile-canvas min-h-screen bg-gradient-to-r flex justify-center items-center py-10 px-4 md:px-8">
                            <div className="w-full max-w-full md:max-w-7xl bg-white rounded-lg shadow-lg p-4 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Profile Section */}
                                <div className="flex flex-col space-y-4">
                                    <h2 className="text-2xl font-bold text-gray-800">Profile</h2>
                                    <div>
                                        <p><span className="font-semibold text-gray-800">Name:</span> {user?.name}</p>
                                        <p><span className="font-semibold text-gray-800">Email:</span> {user?.email}</p>
                                        <p><span className="font-semibold text-grey-800">Subscription:</span> {user?.hasActiveSubscription ? <b className="text-green-800">Active</b> : 'Inactive'}</p>
                                    </div>
                                    {isChangingPassword && (
                                        <div className="flex flex-col space-y-4">
                                            <input onChange={(e) => setCurrentPassword(e.target.value)} type="password" placeholder="Current Password" className="py-2 px-4 bg-gray-100 rounded-lg" />
                                            <input onChange={(e) => setNewPassword(e.target.value)} type="password" placeholder="New Password" className="py-2 px-4 bg-gray-100 rounded-lg" />
                                            <input onChange={(e) => setConfirmPassword(e.target.value)} type="password" placeholder="Confirm Password" className="py-2 px-4 bg-gray-100 rounded-lg" />
                                        </div>
                                    )}
                                    <button onClick={() => handleChangePassword()} className="w-1/2 mt-6 py-2 px-4 bg-orange-600 text-white font-bold rounded-lg hover:bg-orange-700 transition duration-300">
                                        {isChangingPassword ? 'Save Changes' : 'Change Password'}
                                    </button>
                                    <p onClick={() => handleDeleteAccount()} className="text-gray-400 text-sm mt-2 hover:cursor-pointer">
                                        <u>Delete Account</u>
                                    </p>
                                    {user?.hasActiveSubscription === true && user.plan !== 'free' &&
                                        <p onClick={() => {
                                            setUnsubscribeModalOpen(true);
                                            // handleCancelSubscription('too_expensive', 'too_expensive')
                                        }} className="text-gray-400 text-sm mt-2 hover:cursor-pointer">
                                            <u>Cancel subscription</u>
                                        </p>
                                    }
                                    {/* Integrations Section */}
                                    <div className="mt-8">
                                        <h2 className="text-2xl font-bold text-gray-800">Integrations</h2>
                                        <div className="profile-integrations grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                            <div className={`profile-integration-card rounded-xl border p-5 transition-colors ${alphaVantageSecret ? 'border-emerald-200 bg-emerald-50/60' : 'border-slate-200 bg-slate-50'}`}>
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex min-w-0 flex-1 items-start gap-3">
                                                        <div className={`mt-0.5 rounded-lg p-2 ${alphaVantageSecret ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-100'}`}>
                                                            <ChartLine className="h-5 w-5" />
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <h3 className="break-words font-semibold text-slate-900">Alpha Vantage</h3>
                                                                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${alphaVantageSecret ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}>
                                                                    {alphaVantageSecret ? 'Connected' : 'Bring your own key'}
                                                                </span>
                                                            </div>
                                                            <p className="mt-1 break-words text-sm leading-5 text-slate-600">Use your key for current quotes, intraday bars, and options data in chat.</p>
                                                        </div>
                                                    </div>
                                                    {alphaVantageSecret ? (
                                                        <button onClick={handleDisconnectAlphaVantage} disabled={isSavingAlphaVantage} className="text-sm font-medium text-rose-700 hover:text-rose-900 disabled:opacity-50">Disconnect</button>
                                                    ) : (
                                                        <button onClick={() => setShowAlphaVantageForm(true)} className="text-sm font-medium text-indigo-700 hover:text-indigo-900">Connect</button>
                                                    )}
                                                </div>
                                                {showAlphaVantageForm && (
                                                    <div className="mt-4 border-t border-slate-200 pt-4">
                                                        <label className="flex items-center gap-2 text-sm font-medium text-slate-700"><KeyRound className="h-4 w-4" /> Alpha Vantage API key</label>
                                                        <input
                                                            type="password"
                                                            autoComplete="off"
                                                            value={alphaVantageKey}
                                                            onChange={(event) => setAlphaVantageKey(event.target.value)}
                                                            onKeyDown={(event) => { if (event.key === 'Enter') handleSaveAlphaVantage(); }}
                                                            placeholder="Paste your API key"
                                                            className="mt-2 w-full rounded-lg border border-slate-300 bg-white p-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                                            disabled={isSavingAlphaVantage}
                                                        />
                                                        <div className="mt-3 flex items-center justify-between gap-3">
                                                            <a className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:text-indigo-900" href="https://www.alphavantage.co/support/#api-key" target="_blank" rel="noreferrer">Get an API key <ExternalLink className="h-3 w-3" /></a>
                                                            <div className="flex gap-2">
                                                                <button onClick={() => { setShowAlphaVantageForm(false); setAlphaVantageKey(''); }} disabled={isSavingAlphaVantage} className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-200">Cancel</button>
                                                                <button onClick={handleSaveAlphaVantage} disabled={isSavingAlphaVantage || !alphaVantageKey.trim()} className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50">{isSavingAlphaVantage ? 'Saving…' : 'Save key'}</button>
                                                            </div>
                                                        </div>
                                                        <p className="mt-3 text-xs leading-4 text-slate-500">Your key is encrypted at rest. Live US quotes and options availability depend on your Alpha Vantage plan.</p>
                                                    </div>
                                                )}
                                            </div>
                                            {Object.values(IntegrationType).map((t: IntegrationType) => {
                                                if (t === IntegrationType.AlphaVantage) return null;
                                                const userInt = user?.integrations.find(e => e.integrationType == t);
                                                return getIntegrationIcon(t, userInt?.status as 'connected' | 'disconnected')
                                                // return <div key={t} className={`flex items-center p-4 bg-gray-50 ${userInt?.status === 'connected' ? 'border border-green-200' : 'border border-red-200'}`}>
                                                // {getIntegrationIcon(t, userInt?.status as 'connected' | 'disconnected')}
                                                {/* <span className="ml-4 text-gray-800">{ }</span> */ }
                                                // </div>

                                            })}
                                        </div>
                                    </div>

                                    {/* Secrets Section */}
                                    <div className="mt-8">
                                        <div className="flex justify-between items-center mb-4">
                                            <div className="flex items-center">
                                                <h2 className="text-2xl font-bold text-gray-800">Secrets</h2>
                                                <div className="group relative ml-2">
                                                    <InformationCircleIcon className="h-5 w-5 text-gray-500" />
                                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 w-48 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                                        All secrets are encrypted in transit and at rest.
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setShowNewSecretForm(!showNewSecretForm)}
                                                className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                                                title="Add new secret"
                                            >
                                                <Plus className="h-5 w-5 text-gray-600" />
                                            </button>
                                        </div>

                                        {/* New Secret Form */}
                                        {showNewSecretForm && (
                                            <div className="mb-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                                                <div className="flex items-center space-x-2">
                                                    <div className="flex-1">
                                                        <input
                                                            type="text"
                                                            value={newSecretName}
                                                            onChange={(e) => setNewSecretName(e.target.value)}
                                                            placeholder="Secret name"
                                                            className="w-full p-2 border border-gray-300 rounded-lg mb-2"
                                                            disabled={isCreatingSecret}
                                                        />
                                                        <input
                                                            type="text"
                                                            value={newSecretValue}
                                                            onChange={(e) => setNewSecretValue(e.target.value)}
                                                            placeholder="Secret value"
                                                            className="w-full p-2 border border-gray-300 rounded-lg"
                                                            disabled={isCreatingSecret}
                                                        />
                                                    </div>
                                                    <button
                                                        onClick={handleCreateSecret}
                                                        className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-full disabled:opacity-50"
                                                        disabled={isCreatingSecret}
                                                        title="Save secret"
                                                    >
                                                        <Check className="h-5 w-5" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {visibleSecrets.length === 0 && !showNewSecretForm && (
                                            <p className="text-gray-500">No secrets</p>
                                        )}

                                        {!showNewSecretForm && visibleSecrets.length > 0 && (
                                            (
                                                <div className="bg-gray-50 p-4 rounded-lg max-h-60 overflow-y-auto">
                                                    <ul className="divide-y divide-gray-200">
                                                        {visibleSecrets.map((secret) => (
                                                            <li
                                                                key={secret.id}
                                                                className={`py-3 flex flex-col group cursor-pointer hover:bg-gray-100 rounded px-2 ${editingSecretId === secret.id ? 'bg-gray-100' : ''}`}
                                                                onClick={() => editingSecretId !== secret.id && handleRevealSecret(secret.id)} // Only reveal if not editing
                                                            >
                                                                {editingSecretId === secret.id ? (
                                                                    // Edit Mode
                                                                    <div className="w-full space-y-2">
                                                                        <input
                                                                            type="text"
                                                                            value={editingSecretName}
                                                                            onChange={(e) => setEditingSecretName(e.target.value)}
                                                                            placeholder="Secret name"
                                                                            className="w-full p-2 border border-gray-300 rounded-lg"
                                                                            disabled={isUpdatingSecret}
                                                                            onClick={(e) => e.stopPropagation()} // Prevent li click
                                                                        />
                                                                        <input
                                                                            type="text" // Consider type="password" if value shouldn't be visible while editing
                                                                            value={editingSecretValue}
                                                                            onChange={(e) => setEditingSecretValue(e.target.value)}
                                                                            placeholder="Secret value (leave blank to keep unchanged)"
                                                                            className="w-full p-2 border border-gray-300 rounded-lg"
                                                                            disabled={isUpdatingSecret}
                                                                            onClick={(e) => e.stopPropagation()} // Prevent li click
                                                                        />
                                                                        <div className="flex justify-end space-x-2 mt-2">
                                                                            <button
                                                                                onClick={handleCancelEdit}
                                                                                className="p-1 rounded-full hover:bg-gray-300 transition-colors"
                                                                                title="Cancel edit"
                                                                                disabled={isUpdatingSecret}
                                                                            >
                                                                                <X className="h-5 w-5 text-gray-600" />
                                                                            </button>
                                                                            <button
                                                                                onClick={handleUpdateSecret}
                                                                                className="p-1 rounded-full hover:bg-green-200 transition-colors disabled:opacity-50"
                                                                                title="Save changes"
                                                                                disabled={isUpdatingSecret || !editingSecretName || !editingSecretValue}
                                                                            >
                                                                                <Check className="h-5 w-5 text-green-600" />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    // Display Mode
                                                                    <div className="flex justify-between items-center w-full">
                                                                        <div className="flex-1 min-w-0"> {/* Added flex-1 and min-w-0 here */}
                                                                            <p className="font-medium text-gray-800 truncate">{secret.name}</p> {/* Added truncate */}
                                                                            <p className="text-gray-600 font-mono mt-1">
                                                                                {loadingSecrets[secret.id] ? (
                                                                                    <span className="text-gray-400">Loading...</span>
                                                                                ) : revealedSecrets[secret.id] ? (
                                                                                    <span className="font-mono break-all">{revealedSecrets[secret.id]}</span>
                                                                                ) : (
                                                                                    <span className="text-gray-400">••••••••••••••••</span>
                                                                                )}
                                                                            </p>
                                                                        </div>
                                                                        <div className="flex items-center flex-shrink-0 ml-2 space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                            {revealedSecrets[secret.id] ? (
                                                                                <button
                                                                                    className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                                                                                    title="Hide secret"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation(); // Prevent li click
                                                                                        setRevealedSecrets((prev) => {
                                                                                            const newState = { ...prev };
                                                                                            delete newState[secret.id];
                                                                                            return newState;
                                                                                        });
                                                                                    }}
                                                                                >
                                                                                    <EyeOff className="h-5 w-5 text-gray-500" />
                                                                                </button>
                                                                            ) : (
                                                                                <button
                                                                                    className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                                                                                    title="Reveal secret"
                                                                                    onClick={(e) => { e.stopPropagation(); handleRevealSecret(secret.id); }} // Ensure stopPropagation here too
                                                                                >
                                                                                    <Eye className="h-5 w-5 text-gray-500" />
                                                                                </button>
                                                                            )}
                                                                            <button
                                                                                onClick={(event) => handleEditSecret(event, secret)}
                                                                                className="p-1 rounded-full hover:bg-blue-200 transition-colors"
                                                                                title="Edit secret"
                                                                            >
                                                                                <Pencil className="h-5 w-5 text-blue-600" />
                                                                            </button>
                                                                            <button
                                                                                onClick={(event) => handleDeleteSecret(event, secret.id)}
                                                                                className="p-1 rounded-full hover:bg-red-200 transition-colors"
                                                                                title="Delete secret"
                                                                            >
                                                                                <Trash className="h-5 w-5 text-red-600" />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>

                                {/* Subscription Plans */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Pay as You Go Plan */}
                                    <div className="bg-gray-50 p-6 rounded-lg flex flex-col">
                                        <h2 className="text-2xl font-bold mb-2 flex items-center justify-center">
                                            Pay As You Go {user?.plan === 'pay-as-you-go' && <CheckBadgeIcon className="w-6 h-6 text-green-500 ml-2" />}
                                        </h2>
                                        <p className="text-gray-600 mb-4 flex justify-center break-words">Research without limits</p>
                                        <h3 className="text-lg font-semibold text-orange-600 mb-4">What’s included</h3>
                                        <ul className="grid grid-cols-1 gap-2 mb-4 break-words">
                                            <li className="flex"><span className="text-orange-500 mr-2">✓</span> Everything in premium</li>
                                            <li className="flex"><span className="text-orange-500 mr-2">✓</span> Unlimited usage</li>
                                        </ul>
                                        <div className="text-right mt-auto">
                                            <p className="text-4xl font-bold text-gray-900 break-words">
                                                $0 <span className="text-sm text-gray-500">USD/month</span>
                                            </p>
                                            <button
                                                onClick={() => handleCreateSubscription(process.env.REACT_APP_PQ_PAY_AS_YOU_GO_PRICE_ID)}
                                                disabled={user?.hasActiveSubscription}
                                                className={`mt-4 py-2 px-4 bg-orange-600 text-white font-bold rounded-lg transition duration-300 ${user?.hasActiveSubscription ? 'opacity-75' : 'hover:bg-orange-700 hover:cursor-pointer'}`}
                                            >
                                                Buy now
                                            </button>
                                        </div>
                                    </div>
                                    {/* Premium Plan */}
                                    <div className="bg-gray-50 p-6 rounded-lg flex flex-col">
                                        <h2 className="text-2xl font-bold mb-2 flex items-center justify-center">
                                            Premium Plan {user?.plan === 'premium' && <CheckBadgeIcon className="w-6 h-6 text-green-500 ml-2" />}
                                        </h2>
                                        <p className="text-gray-600 mb-4 flex justify-center break-words">Faster, smarter investments</p>
                                        <h3 className="text-lg font-semibold text-orange-600 mb-4">What’s included</h3>
                                        <ul className="grid grid-cols-1 gap-2 mb-4 break-words">
                                        <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> 250 monthly prompts</li>
                                        <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> Earnings reports since 2009</li>
                                        <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> Transcripts since 2021</li>
                                        <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> 10 Workflow executions per month</li>
                                        <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> Feature/Data source requests</li>
                                        </ul>
                                        <div className="text-right mt-auto">
                                            <p className="text-4xl font-bold text-gray-900 break-words">
                                                $20 <span className="text-sm text-gray-500">USD/month</span>
                                            </p>
                                            <button
                                                onClick={() => handleCreateSubscription(process.env.REACT_APP_PQ_PREMIUM_PRICE_ID)}
                                                disabled={user?.hasActiveSubscription}
                                                className={`mt-4 py-2 px-4 bg-orange-600 text-white font-bold rounded-lg transition duration-300 ${user?.hasActiveSubscription ? 'opacity-75' : 'hover:bg-orange-700 hover:cursor-pointer'}`}
                                            >
                                                Buy now
                                            </button>
                                        </div>
                                    </div>

                                    {/* Pro Plan */}
                                    {/* <div className="bg-gray-50 p-6 rounded-lg flex flex-col">
                                        <h2 className="text-2xl font-bold mb-2 flex items-center justify-center">
                                            Pro Plan {user?.plan === 'pro' && <CheckBadgeIcon className="w-6 h-6 text-green-500 ml-2" />}
                                        </h2>
                                        <p className="text-gray-600 mb-4 flex justify-center break-words">Automate investment research</p>
                                        <h3 className="text-lg font-semibold text-orange-600 mb-4">What’s included</h3>
                                        <ul className="grid grid-cols-1 gap-2 mb-4 break-words">
                                            <li className="flex"><span className="text-orange-500 mr-2">✓</span> 250 monthly prompts</li>
                                            <li className="flex"><span className="text-orange-500 mr-2">✓</span> Access to o3-mini</li>
                                            <li className="flex"><span className="text-orange-500 mr-2">✓</span> Earnings reports since 2009</li>
                                            <li className="flex"><span className="text-orange-500 mr-2">✓</span> Transcripts since 2021</li>
                                            <li className="flex"><span className="text-orange-500 mr-2">✓</span> 10 Workflow executions per month (unlimited tests)</li>
                                            <li className="flex"><span className="text-orange-500 mr-2">✓</span> Feature/Data source requests</li>
                                        </ul>
                                        <div className="text-right mt-auto">
                                            <p className="text-4xl font-bold text-gray-900 break-words">
                                                $49 <span className="text-sm text-gray-500">USD/month</span>
                                            </p>
                                            <button
                                                onClick={() => handleCreateSubscription(process.env.REACT_APP_PQ_PRO_PRICE_ID)}
                                                disabled={user?.hasActiveSubscription}
                                                className={`mt-4 py-2 px-4 bg-orange-600 text-white font-bold rounded-lg transition duration-300 ${user?.hasActiveSubscription ? 'opacity-75' : 'hover:bg-orange-700 hover:cursor-pointer'}`}
                                            >
                                                Buy now
                                            </button>
                                        </div>
                                    </div> */}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Toolbar always visible at bottom */}
                <div className="w-full">
                    <ToolbarComponent
                        handleUpdateConversation={handleUpdateConversation}
                        convo={convo}
                        convos={convos}
                        handleConversationClick={(id: string) => {
                            const queryParams = new URLSearchParams(window.location.search);
                            queryParams.set('conversationId', id);
                            navigate('/dashboard' + '?' + queryParams.toString());
                            navigate(0);
                        }}
                        handleCreateConversation={handleCreateConversation}
                        handleDeleteConversation={handleDeleteConversation}
                        supportedTickers={supportedTickers}
                        handleDeleteShareLink={handleDeleteShareLink}
                        handleCreateShareLink={handleCreateShareLink}
                    />
                </div>
            </div>
        </>
    )
}

const CancelSubscriptionModal = ({
    isOpen,
    setIsOpen,
}: {
    isOpen: boolean,
    setIsOpen: (val: boolean) => void
}) => {
    const [feedback, setSelectedFeedback] = useState('');
    const [additionalComments, setAdditionalComments] = useState('');
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.user.user);

    const handleFeedbackChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedFeedback(e.currentTarget.value);
    };

    const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setAdditionalComments(e.target.value);
    };

    const handleCancelSubscription = async (e: any) => {
        e.preventDefault();
        if (!user?.hasActiveSubscription) {
            dispatch(setNotification({ message: 'No active subscription to cancel', type: 'error' }));
            return;
        }

        const res = await dispatch(cancelSubscription({ reason: additionalComments, feedback: feedback }));
        dispatch(getUser());
        if (res.meta.requestStatus === 'fulfilled') {
            dispatch(setNotification({ message: 'Subscription cancelled successfully', type: 'success' }));
        } else {
            dispatch(setNotification({ message: 'Unable to cancel subscription', type: 'error' }));
        }
        setIsOpen(false);
    }


    return (
        <div className={`${!isOpen ? 'hidden' : ''} z-50 fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center p-4`}>
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
                <h2 className="text-2xl font-bold mb-4 text-gray-800">Cancel Subscription</h2>
                <form onSubmit={async (e) => {
                    await handleCancelSubscription(e);
                }}>
                    <div className="mb-4">
                        <label className="block text-gray-700 font-medium mb-2" htmlFor="reason">
                            Why are you canceling?
                        </label>
                        <select
                            id="reason"
                            value={feedback}
                            onChange={handleFeedbackChange}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
                            required
                        >
                            <option value="" disabled>Select a reason</option>
                            <option value="too_expensive">Too expensive</option>
                            <option value="missing_features">Missing features</option>
                            <option value="switched_service">Switched to another service</option>
                            <option value="unused">Not using the service</option>
                            <option value="customer_service">Issues with customer service</option>
                            <option value="too_complex">Too complex</option>
                            <option value="low_quality">Low quality</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-700 font-medium mb-2" htmlFor="comments">
                            Additional comments
                        </label>
                        <textarea
                            id="comments"
                            value={additionalComments}
                            onChange={handleReasonChange}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
                            rows={4}
                        ></textarea>
                    </div>
                    <div className="flex justify-end space-x-4">
                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400 transition duration-300"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition duration-300"
                        >
                            Submit
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
